import React from 'react'

import Metadata from '../components/Metadata'
import ShortStoryPage from '../components/Story/ShortStoryPage'
import StoryPage from '../components/Story/StoryPage'
import { logServerSidePropsError } from '../components/utilities/log-helper'
import regions, { Region } from '../config/regions'
import { STATUS_CODE } from '../data/enums/statusCodes'
import fetchTopStories from '../data/query/fetchTopStories'
import fetchMoreStories from '../data/query/storypage/fetchMoreStories'
import fetchStoryPageData from '../data/query/storypage/fetchStoryPageData'
import storyListFieldsSelector from '../data/selectors/storyListFieldsSelector'
import extractMoreStory from '../data/transformers/extractMoreStory'
import { default as TRegion } from '../data/types/region'
import { StoryPreview, SurrogateKeys, TopStory } from '../types'
import { RouteRedMetrics } from '../utils/metrics'

import Head from 'next/head'

export interface StoryPageProps {
  region: TRegion
  relatedStories: StoryPreview[]
  moreStories: StoryPreview[]
  story: TopStory
  storyCategory: string
}

export function isSlugValid(slug: any) {
  if (
    slug?.indexOf('.') > -1 ||
    typeof slug !== 'string' ||
    slug.trim() === '' ||
    ['undefined', 'null'].includes(slug) ||
    slug.length < 1 ||
    slug.length > 256 ||
    !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)
  ) {
    return false
  }
  return true
}

export default ({ story, storyCategory, moreStories, relatedStories, region }: StoryPageProps) => {
  return (
    <>
      <Head>
        <title>{story.metadata.title || 'News 12'}</title>
        <link rel='canonical' href={`https://${region.name}.news12.com/${story.slug}`} />
        <meta property='category' content={storyCategory} />
      </Head>
      <Metadata
        metadata={{
          ...{
            title: story.metadata.title,
            description: story.metadata.description,
            thumbnailUrl: story.metadata.thumbnailUrl
          }
        }}
      />
      {!story.body && !story.content ? (
        <ShortStoryPage story={story} moreStories={moreStories} />
      ) : (
        <StoryPage story={story} moreStories={moreStories} relatedStories={relatedStories} />
      )}
    </>
  )
}

export async function getServerSideProps(context) {
  const redMetrics: RouteRedMetrics = new RouteRedMetrics({
    method: 'GET',
    route: 'slug-x'
  }).start()

  try {
    const surrogateKeyHeader = context?.res?.getHeader('Surrogate-Key')?.split(' ')
    const surrogateKeys: SurrogateKeys = [...surrogateKeyHeader]

    const regionName = context?.req?.headers?.host?.split('.')?.[0]
    const { slug } = context?.params

    if (!isSlugValid(slug)) {
      redMetrics.end({
        status: 404,
        error: 'Slug not found'
      })

      return { notFound: true }
    }

    const region = regions[regionName] || regions[Region.DEFAULT]

    let story: TopStory | null
    try {
      story = await fetchStoryPageData(slug)
    } catch (error) {
      redMetrics.end({
        status: 500,
        error: 'Failed to get story data'
      })
      logServerSidePropsError(error)

      return { notFound: true }
    }

    if (!story) {
      redMetrics.end({
        status: 404,
        error: 'Story data not found'
      })

      return { notFound: true }
    }

    const [moreStoriesCategory, relatedStoriesCategory] = story.categories
    const storyCategory = story.categories?.map(category => category?.name).join(' ') || 'No category'
    const isRegionNewYork = region.name === Region.NEW_YORK
    const regionId = isRegionNewYork ? null : region.contentfulId

    let moreStories: StoryPreview[] = []
    try {
      const { statusCode, stories, entryIDs } = await fetchMoreStories({
        regionId,
        slugExclusion: story.slug,
        categoryId: moreStoriesCategory.id,
        select: storyListFieldsSelector,
        storyExtractor: extractMoreStory
      })
      if (statusCode === STATUS_CODE.success) {
        surrogateKeys.push(...entryIDs)
        moreStories = stories
      }
    } catch (error) {
      logServerSidePropsError(error)
    }

    let relatedStories: StoryPreview[] = []
    try {
      if (relatedStoriesCategory) {
        const { statusCode, stories, entryIDs } = await fetchMoreStories({
          regionId,
          slugExclusion: story.slug,
          categoryId: relatedStoriesCategory.id,
          select: storyListFieldsSelector,
          storyExtractor: extractMoreStory
        })
        if (statusCode === STATUS_CODE.success) {
          surrogateKeys.push(...entryIDs)
          relatedStories = stories
        }
      } else {
        const { statusCode, topStories, entryIDs } = await fetchTopStories({ topStoriesId: region.topStoriesId })

        if (statusCode === STATUS_CODE.success) {
          surrogateKeys.push(...(entryIDs as SurrogateKeys).filter((id: string) => id !== story?.id))
          relatedStories = topStories.filter((topStory: StoryPreview) => topStory?.id !== story?.id) as StoryPreview[]
        }
      }
    } catch (error) {
      logServerSidePropsError(error)
    }

    redMetrics.end({
      status: STATUS_CODE.success
    })

    context?.res?.setHeader('Surrogate-Key', [story.id, ...surrogateKeys].filter(Boolean).join(' '))

    return {
      props: {
        region,
        relatedStories,
        moreStories,
        story,
        storyCategory
      }
    }
  } catch (error) {
    redMetrics.end({
      status: 500,
      error: 'Failed to load Story page data'
    })
    logServerSidePropsError(error)

    return { notFound: true }
  }
}
